import { Metric } from '@fullstory/proto/fs/services/search-settings';
import { isEqual } from 'lodash';
import {
  AppDispatch,
  ComputeMetricResponseWithId,
  DataTableWithId,
  fullstorySelectors,
  getSearchCacheKey,
  MetricsRequestActions,
} from '@fullstory/redux';
import { isNonNull } from '@fullstory/typeutils';
import { UID } from '@fullstory/util/uid';
import jstz from 'jstz';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RendererClient } from '@fullstory/renderer-react';
import { getMetricForEvent } from '@fullstory/signal-studio-context';
import { makePlatformSelectorDeps } from '../utils';

// returns a fetcher to compute metric
export const useMetricFetcher = () => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const searchCacheKey = useSelector(getSearchCacheKey);

  return useCallback(
    async (definition: Metric | null, force = false) => {
      if (!definition) return [];
      const uid = UID();
      const payload = {
        body: { orgId, definition },
      };

      const data = await dispatch(
        MetricsRequestActions.computeMetric(
          {
            ...{ ...payload.body, isRefresh: force },
            timezone: jstz.determine().name(),
          },
          searchCacheKey,
          Math.floor(Math.random() * 1000000),
          force,
          uid,
        ),
      );
      return data;
    },
    [dispatch, orgId, searchCacheKey],
  );
};

const dataTablesWithIdToVolume = (data: DataTableWithId[]): number[] => {
  const volume: number[] = [];

  data.forEach(table => {
    table.table.forEach(row => {
      volume.push(row.value);
    });
  });
  return volume;
};

export const responseToVolume = (response: ComputeMetricResponseWithId[]): number[] =>
  dataTablesWithIdToVolume(response.map(d => d.data).filter(isNonNull));

const defaultGetMetricForSelector = (selector: string) =>
  getMetricForEvent({
    event: { primary: null, deps: makePlatformSelectorDeps({ selector, platforms: [] }) },
  });

export const useOptimizeUtil = (
  getMetricForSelector: (selector: string) => Metric | null = defaultGetMetricForSelector,
  rendererClient?: RendererClient | null,
  // allow for a 1% variation in event volume per bucket to account for any noise
  matchTolerance: number = 0.01,
) => {
  // this is probably cached somewhere along the way, but let's cache it here just to be super safe.
  // matchSelectors will try to fetch the original selector on every match, so we DEFINITELY want to cache it.
  const fetchCache = useRef<Map<string, number[]>>(new Map());
  const fetchMatches = useMetricFetcher();

  const fetchVolume = useCallback(
    async (selector: string): Promise<number[]> => {
      if (fetchCache.current.has(selector)) {
        return fetchCache.current.get(selector)!;
      }
      const volumeData = await fetchMatches(getMetricForSelector(selector), true);
      fetchCache.current.set(selector, responseToVolume(volumeData));
      return responseToVolume(volumeData);
    },
    [fetchMatches, getMetricForSelector],
  );

  // if the getMetric fn changes, we need to reset the cache as we may no longer be producing the same requests by selector
  useEffect(() => {
    fetchCache.current = new Map();
  }, [getMetricForSelector]);

  const fetchTotalVolume = useCallback(
    async (selector: string): Promise<number> => {
      const volume = await fetchVolume(selector);
      return volume.reduce((acc, v) => acc + v, 0);
    },
    [fetchVolume],
  );

  // tolerance is a percentage of the volume that is allowed to be different, between 0 and 1.
  const matchVolumes = useCallback((v1: number[], v2: number[], tolerance: number = 0) => {
    // if either volume has all 0s, return false.
    if (v1.every(v => v === 0) || v2.every(v => v === 0)) {
      return false;
    }
    // skip last volume because the volume could technically change while the algorithm is running.
    for (let i = 0; i < v1.length - 1; i++) {
      const volume1 = v1[i];
      const volume2 = v2[i];
      if (volume1 === undefined || volume2 === undefined) {
        return false;
      }

      if (Math.abs(volume1 - volume2) > volume1 * tolerance) {
        return false;
      }
    }

    return true;
  }, []);

  // it's okay if we don't have the rendererClient, we just won't use that optimization.
  const matchSelectors = useCallback(
    async (s1: string, s2: string) => {
      // if the preview changes mid-match, trim can hang indefinitely.
      // we'll add a timeout for the renderer query, which will throw an error and cause trim to early return.
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('renderer client timed out')), 1000),
      );

      const domMatches1Promise = rendererClient?.queryObjects({
        patterns: [{ type: 'selector', selector: s1 }],
      });

      const domMatches2Promise = rendererClient?.queryObjects({
        patterns: [{ type: 'selector', selector: s2 }],
      });

      const domMatches1 = await Promise.race([domMatches1Promise, timeoutPromise]);
      const domMatches2 = await Promise.race([domMatches2Promise, timeoutPromise]);

      if (domMatches1 && domMatches2) {
        // we can confidently use the DOM to say that selector's DON'T match.
        // we can't say determine they DO match, because the selector can exist on other pages.
        const nodes1 = Object.keys(domMatches1 || {});
        const nodes2 = Object.keys(domMatches2 || {});
        if (!isEqual(nodes1, nodes2)) {
          return false;
        }
      }

      const v1 = await fetchVolume(s1);
      const v2 = await fetchVolume(s2);

      return matchVolumes(v1, v2, matchTolerance);
    },
    [rendererClient, fetchVolume, matchVolumes, matchTolerance],
  );

  return { fetchVolume, matchVolumes, matchSelectors, fetchTotalVolume };
};
